<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useCategoryEditorContext } from '@/composition/catalog/use-category-editor';
  import V2CatalogEditorSection from '../shared/V2CatalogEditorSection.vue';
  import V2CatalogStationChips from '../shared/V2CatalogStationChips.vue';

  /**
   * «Impresión» of a category (plan §5.15): the stations its products print
   * on (the default shown selected on create), and the products that use
   * their own stations, each one link away.
   */
  const OWN_PRINTERS_SHOWN = 4;

  const editor = useCategoryEditorContext();
  const { data, url, layout } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const form = computed(() => editor.form.value);

  /** Its products with stations of their own. */
  const ownPrinters = computed(() => {
    const id = editor.categoryId;
    if (id === null) return [];
    return data.items.value.filter(
      (item) => item.categoryId === id && Array.isArray(item.comandaPrinters)
    );
  });
  const shownOwn = computed(() =>
    ownPrinters.value.slice(0, OWN_PRINTERS_SHOWN)
  );
  const moreOwn = computed(() =>
    Math.max(0, ownPrinters.value.length - OWN_PRINTERS_SHOWN)
  );

  function openProduct(itemId: number) {
    url.openItem(itemId, layout.columns.value.asideDocked);
  }
</script>

<template>
  <V2CatalogEditorSection
    v-if="form"
    class="v2-catalog-category-printing-section"
    :title="t('menuPage.printing.title')"
    :description="t('menuPage.printing.categoryLead')"
  >
    <V2CatalogStationChips
      :value="form.comandaPrinters"
      :label="t('menuPage.printing.stations')"
      :empty-warning="t('menuPage.printing.categoryNone')"
      :disabled="editor.readOnly.value"
      @input="editor.update('comandaPrinters', $event)"
    />
    <p v-if="ownPrinters.length" class="printing-own">
      {{
        tc('menuPage.printing.ownPrinters', ownPrinters.length, {
          n: ownPrinters.length,
        })
      }}
      <template v-for="(item, index) in shownOwn">
        <button
          :key="item.id"
          type="button"
          class="own-link"
          @click="openProduct(item.id)"
        >
          {{ item.name }}</button
        ><span v-if="index < shownOwn.length - 1" :key="`s${item.id}`">, </span>
      </template>
      <span v-if="moreOwn">
        {{ tc('menuPage.printing.andMore', moreOwn, { n: moreOwn }) }}</span
      >
    </p>
  </V2CatalogEditorSection>
</template>

<style scoped lang="scss">
  .v2-catalog-category-printing-section {
    .printing-own {
      @apply tw-m-0 tw-mt-3 tw-text-sm tw-leading-relaxed tw-text-neutral-600;

      .own-link {
        @apply tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-text-neutral-900 tw-underline;

        &:focus-visible {
          @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }
  }
</style>
